<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from '../shared/IntegrationEmptyState.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';
import NewHook from '../NewHook.vue';

const PROVIDERS = {
  tablefever: {
    i18nKey: 'TABLEFEVER',
    logo: 'tablefever.png',
    icon: 'i-lucide-utensils',
  },
  sevenrooms: {
    i18nKey: 'SEVENROOMS',
    logo: 'sevenrooms.png',
    icon: 'i-lucide-calendar-check',
  },
};

const SECRET_FIELD_PATTERN = /token|password|secret/i;

const route = useRoute();
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const providerId = computed(() => route.params.provider);
const providerMeta = computed(() => PROVIDERS[providerId.value] || null);

const uiFlags = computed(() => getters['integrations/getUIFlags']?.value || {});
const getIntegration = getters['integrations/getIntegration'];

const integration = computed(
  () => getIntegration.value(providerId.value) || {}
);
const hooks = computed(() => integration.value.hooks || []);
const isLoading = computed(() => uiFlags.value.isFetching);
const isEmpty = computed(() => !hooks.value.length);

const showAddHookModal = ref(false);
const showDetailsModal = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedHook = ref(null);
const selectedHookDetails = ref([]);

const providerName = computed(() =>
  providerMeta.value
    ? t(
        `INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDERS.${providerMeta.value.i18nKey}.NAME`
      )
    : ''
);

const providerDescription = computed(() =>
  providerMeta.value
    ? t(
        `INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDERS.${providerMeta.value.i18nKey}.DESCRIPTION`
      )
    : ''
);

const isSecretField = name => SECRET_FIELD_PATTERN.test(name || '');

const buildDetails = hook => {
  const formSchema = integration.value.settings_form_schema || [];
  const labelByName = Object.fromEntries(
    formSchema.map(field => [field.name, field.label])
  );
  const visibleProperties = integration.value.visible_properties || [];
  const secretProperties = formSchema
    .map(field => field.name)
    .filter(name => isSecretField(name) && !visibleProperties.includes(name));
  const propertyNames = [...visibleProperties, ...secretProperties];

  return propertyNames.map(property => ({
    label: labelByName[property] || property,
    value: hook?.settings?.[property] || '',
    masked: isSecretField(property),
  }));
};

const hookDisplayName = hook =>
  hook?.settings?.name ||
  hook?.settings?.venue_id ||
  hook?.settings?.resto_hash_id ||
  `#${hook?.id}`;

onMounted(() => {
  store.dispatch('integrations/get');
});

const openConnectModal = () => {
  showAddHookModal.value = true;
};

const hideConnectModal = () => {
  showAddHookModal.value = false;
};

const openDetailsModal = hook => {
  selectedHook.value = hook;
  selectedHookDetails.value = buildDetails(hook);
  showDetailsModal.value = true;
};

const hideDetailsModal = () => {
  showDetailsModal.value = false;
  selectedHook.value = null;
  selectedHookDetails.value = [];
};

const openDisconnectPopup = hook => {
  selectedHook.value = hook;
  showDeleteConfirmationPopup.value = true;
};

const closeDisconnectPopup = () => {
  showDeleteConfirmationPopup.value = false;
  selectedHook.value = null;
};

const confirmDisconnect = async () => {
  if (!selectedHook.value) return;
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: selectedHook.value.id,
      appId: selectedHook.value.app_id,
    });
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
    closeDisconnectPopup();
    hideDetailsModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE')
    );
  }
};

const copyDetailValue = async value => {
  await copyTextToClipboard(value);
  useAlert(t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.COPIED'));
};
</script>

<template>
  <SettingsLayout
    v-if="providerMeta"
    :header-title="providerName"
    :button-label="
      isEmpty
        ? $t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')
        : $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.ADD_ANOTHER')
    "
    @click="openConnectModal"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <IntegrationEmptyState
      v-else-if="isEmpty"
      :icon="providerMeta.icon"
      :title="providerName"
      :description="providerDescription"
      :button-label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
      @action="openConnectModal"
    />

    <div v-else class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">{{ providerDescription }}</p>
      <div
        v-for="hook in hooks"
        :key="hook.id"
        class="flex items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-3 p-4"
      >
        <div class="min-w-0">
          <p class="truncate text-sm font-medium text-n-slate-12">
            {{ hookDisplayName(hook) }}
          </p>
          <p
            v-if="hook.settings?.slug"
            class="truncate text-xs text-n-slate-10"
          >
            {{ hook.settings.slug }}
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <Button
            slate
            faded
            size="sm"
            :label="$t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.VIEW_DETAILS')"
            @click="openDetailsModal(hook)"
          />
          <Button
            ruby
            faded
            size="sm"
            :label="$t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT')"
            @click="openDisconnectPopup(hook)"
          />
        </div>
      </div>
    </div>

    <woot-modal v-model:show="showAddHookModal" :on-close="hideConnectModal">
      <NewHook :integration-id="providerId" @close="hideConnectModal" />
    </woot-modal>

    <woot-modal v-model:show="showDetailsModal" :on-close="hideDetailsModal">
      <div v-if="selectedHook" class="flex h-auto flex-col">
        <woot-modal-header
          :header-title="hookDisplayName(selectedHook)"
          :header-content="
            $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.DETAILS_DESCRIPTION')
          "
        />
        <div class="px-8 pb-8">
          <dl class="flex flex-col gap-4">
            <div
              v-for="detail in selectedHookDetails"
              :key="detail.label"
              class="flex flex-col gap-1"
            >
              <dt
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ detail.label }}
              </dt>
              <dd v-if="detail.masked">
                <AccessToken
                  :value="detail.value || '--'"
                  :show-reset-button="false"
                  :copy-label="
                    $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.COPY')
                  "
                  @on-copy="copyDetailValue"
                />
              </dd>
              <dd v-else class="break-all text-sm text-n-slate-12">
                {{ detail.value || '--' }}
              </dd>
            </div>
          </dl>
          <div class="mt-6 flex justify-end">
            <Button
              slate
              faded
              :label="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
              @click="hideDetailsModal"
            />
          </div>
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDisconnectPopup"
      :on-confirm="confirmDisconnect"
      :title="$t('INTEGRATION_APPS.DELETE.TITLE.ACCOUNT')"
      :message="$t('INTEGRATION_APPS.DELETE.MESSAGE.ACCOUNT')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.ACCOUNT')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </SettingsLayout>

  <div v-else class="flex items-center justify-center py-12 text-n-slate-11">
    {{ $t('INTEGRATION_SETTINGS.RESTAURANT_WIDGETS.PROVIDER_UNKNOWN') }}
  </div>
</template>
